import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  Stack,
  Box,
  Text,
  Textarea,
  Alert,
  AlertIcon,
  Flex,
  useToast,
  VStack,
  HStack,
  Badge,
  Divider,
  Icon,
  Tooltip,
  useColorModeValue,
  Avatar,
  Center,
  SimpleGrid,
} from '@chakra-ui/react';
import {
  DownloadIcon,
  CheckCircleIcon,
  CloseIcon,
  TimeIcon,
  WarningIcon,
  AttachmentIcon,
  CalendarIcon,
} from '@chakra-ui/icons';
import {
  FaFileExcel,
  FaProjectDiagram,
  FaMapMarkerAlt,
  FaSortAlphaDown,
} from 'react-icons/fa';
import axiosInstance from 'axiosInstance';
import baseUrl from 'Base_Url/baseUrl';

const FileDetailsModal = ({
  isOpen,
  onClose,
  selectedFile,
  onFileUpdate,
  user,
}) => {
  const toast = useToast();
  const [notes, setNotes] = useState('');
  const [noteError, setNoteError] = useState('');
  const [isRejecting, setIsRejecting] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const modalBg = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.800', 'white');
  const subtitleColor = useColorModeValue('gray.600', 'gray.300');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return '-';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'green';
      case 'rejected':
        return 'red';
      case 'pending':
        return 'orange';
      case 'submitted':
        return 'blue';
      default:
        return 'gray';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved':
        return CheckCircleIcon;
      case 'rejected':
        return CloseIcon;
      case 'pending':
        return TimeIcon;
      case 'submitted':
        return WarningIcon;
      default:
        return WarningIcon;
    }
  };

  const truncateText = (text, maxLength = 40) => {
    if (!text) return '-';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const getFileTypeDisplay = (fileType) => {
    if (!fileType?.name) return '-';

    const isMobile = window.innerWidth < 768;
    const maxLength = isMobile ? 25 : 35;

    if (fileType.name.length > maxLength) {
      return (
        <Tooltip label={fileType.name} hasArrow>
          <Text as="span" fontWeight="semibold">
            {truncateText(fileType.name, maxLength)}
          </Text>
        </Tooltip>
      );
    }

    return fileType.name;
  };

  const getFileNameDisplay = (name) => {
    if (!name) return 'Untitled File';

    const isMobile = window.innerWidth < 768;
    const maxLength = isMobile ? 30 : 50;

    if (name.length > maxLength) {
      return (
        <Tooltip label={name} hasArrow placement="top">
          <Text as="span" noOfLines={1}>
            {truncateText(name, maxLength)}
          </Text>
        </Tooltip>
      );
    }

    return name;
  };

  const handleAction = async (fileId, actionType) => {
    if (!fileId) return;

    if (actionType === 'reject' && (!notes || notes.trim() === '')) {
      setNoteError('Note is required');

      const textarea = document.getElementById('reject-notes');
      if (textarea) {
        textarea.classList.remove('shake');
        void textarea.offsetWidth;
        textarea.classList.add('shake');
      }
      return;
    }

    try {
      setActionLoading(actionType);

      const token = localStorage.getItem('token');
      const res = await axiosInstance.put(
        `${baseUrl}/upload-file/${fileId}/status`,
        {
          fileId,
          status: actionType,
          rejectionComments: notes,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (res.data?.status) {
        onFileUpdate();
        onClose();
        setNotes('');
        setIsRejecting(false);
        setNoteError('');

        toast({
          title: `File ${
            actionType === 'approved' ? 'approved' : 'rejected'
          } successfully`,
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      }
    } catch (err) {
      if (err.response?.status !== 401) {
        toast({
          title:
            err.response?.data?.message ||
            err.response?.data?.data ||
            'Please try again.',
          status: 'error',
          duration: 10000,
          isClosable: true,
          position: 'top-right',
        });
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!selectedFile?._id) return;

    try {
      const token = localStorage.getItem('token');

      const response = await axiosInstance.get(
        `${baseUrl}/upload-file/download/${selectedFile._id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          responseType: 'blob',
        },
      );

      let fileName = 'file.xlsx';
      const contentDisposition = response.headers['content-disposition'];

      if (contentDisposition) {
        let matches = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
        if (matches && matches[1]) {
          fileName = decodeURIComponent(matches[1]);
        } else {
          matches = contentDisposition.match(/filename="?([^"]+)"?/i);
          if (matches && matches[1]) {
            fileName = matches[1];
          }
        }
      }

      const blob = new Blob([response.data], {
        type: response.headers['content-type'] || 'application/octet-stream',
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.style.display = 'none';

      document.body.appendChild(link);
      link.click();

      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);

      toast({
        title: 'Download started',
        description: `File download initiated`,
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
    } catch (err) {
      let errorMessage = 'Failed to download file';
      if (err.response?.status === 403) {
        errorMessage = 'You do not have permission to download this file';
      } else if (err.response?.status === 404) {
        errorMessage = 'File not found';
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      }

      toast({
        title: 'Download failed',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
    }
  };

  return (
    <>
      <style>
        {`
          @keyframes shake {
            0% { transform: translateX(0); }
            25% { transform: translateX(-4px); }
            50% { transform: translateX(4px); }
            75% { transform: translateX(-4px); }
            100% { transform: translateX(0); }
          }
          .shake {
            animation: shake 0.3s;
          }
          .truncate {
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
        `}
      </style>

      <Modal
        isOpen={isOpen}
        onClose={onClose}
        isCentered
        size={{ base: 'full', md: 'lg' }}
        motionPreset="slideInBottom"
      >
        <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
        <ModalContent
          borderRadius={{ base: 0, md: 'xl' }}
          overflow="hidden"
          boxShadow={{ base: 'none', md: '0 20px 60px rgba(0, 0, 0, 0.3)' }}
          bg={modalBg}
          maxW={{ base: '100%', md: '600px' }}
          margin={{ base: 0, md: 4 }}
          maxH={{ base: '100vh', md: '90vh' }}
        >
          <ModalHeader
            pb={3}
            borderBottom="1px solid"
            borderColor={borderColor}
            pt={{ base: 4, md: 3 }}
          >
            <Flex justify="space-between" align="center" wrap="wrap" gap={2}>
              <VStack align="flex-start" spacing={0}>
                <Text fontSize="lg" fontWeight="bold" color={textColor}>
                  File Details
                </Text>
                <Text fontSize="sm" color={subtitleColor}>
                  View and manage file information
                </Text>
              </VStack>

              {selectedFile?.status && (
                <Badge
                  colorScheme={getStatusColor(selectedFile.status)}
                  size="lg"
                  px={3}
                  py={1}
                  borderRadius="full"
                  display="flex"
                  alignItems="center"
                  gap={1}
                >
                  <Icon as={getStatusIcon(selectedFile.status)} boxSize={3} />
                  <Text fontSize="xs" fontWeight="bold">
                    {selectedFile.status.charAt(0).toUpperCase() +
                      selectedFile.status.slice(1)}
                  </Text>
                </Badge>
              )}
            </Flex>
          </ModalHeader>

          <ModalCloseButton
            top={{ base: 3, md: 4 }}
            right={{ base: 3, md: 4 }}
            boxShadow="md"
            size={{ base: 'md', md: 'lg' }}
          />

          <ModalBody
            py={{ base: 4, md: 6 }}
            px={{ base: 4, md: 6 }}
            overflowY="auto"
            maxH={{ base: 'calc(100vh - 200px)', md: 'auto' }}
          >
            {selectedFile ? (
              <Stack spacing={{ base: 4, md: 6 }}>
                <Flex
                  align="center"
                  p={{ base: 3, md: 4 }}
                  borderRadius="lg"
                  borderColor="blue.100"
                  direction={{ base: 'column', sm: 'row' }}
                  gap={{ base: 3, sm: 0 }}
                >
                  <Flex
                    align="center"
                    justify="center"
                    w={{ base: '40px', md: '48px' }}
                    h={{ base: '40px', md: '48px' }}
                    borderRadius="lg"
                    bg="blue.500"
                    mr={{ base: 0, sm: 4 }}
                  >
                    <AttachmentIcon
                      color="white"
                      boxSize={{ base: 5, md: 6 }}
                    />
                  </Flex>
                  <Box
                    textAlign={{ base: 'center', sm: 'left' }}
                    flex="1"
                    minW={0}
                  >
                    <Text
                      fontWeight="bold"
                      color={textColor}
                      fontSize={{ base: 'md', md: 'lg' }}
                      mb={1}
                      className="truncate"
                    >
                      {getFileNameDisplay(
                        selectedFile.name || selectedFile.filename,
                      )}
                    </Text>
                    <HStack
                      spacing={3}
                      justify={{ base: 'center', sm: 'flex-start' }}
                      wrap="wrap"
                    >
                      <Badge
                        colorScheme="blue"
                        fontSize="xs"
                        maxW="150px"
                        className="truncate"
                      >
                        {getFileTypeDisplay(selectedFile?.fileType)}
                      </Badge>
                      <Text fontSize="xs" color={subtitleColor}>
                        Uploaded {formatDate(selectedFile.uploadedDate)}
                      </Text>
                    </HStack>
                  </Box>
                </Flex>

                <SimpleGrid
                  columns={{ base: 1, sm: 2 }}
                  spacing={{ base: 3, md: 4 }}
                >
                  <VStack align="start" spacing={1}>
                    <HStack spacing={2}>
                      <Icon
                        as={FaProjectDiagram}
                        color="teal.500"
                        boxSize={4}
                      />
                      <Text
                        fontSize="sm"
                        color={subtitleColor}
                        fontWeight="medium"
                      >
                        Scheme
                      </Text>
                    </HStack>
                    <Text
                      fontWeight="semibold"
                      color={textColor}
                      fontSize="sm"
                      className="truncate"
                      title={selectedFile?.scheme?.name}
                    >
                      {selectedFile?.scheme?.name || '-'}
                    </Text>
                  </VStack>

                  <VStack align="start" spacing={1}>
                    <HStack spacing={2}>
                      <Icon
                        as={FaMapMarkerAlt}
                        color="purple.500"
                        boxSize={4}
                      />
                      <Text
                        fontSize="sm"
                        color={subtitleColor}
                        fontWeight="medium"
                      >
                        Taluka
                      </Text>
                    </HStack>
                    <Text
                      fontWeight="semibold"
                      color={textColor}
                      fontSize="sm"
                      className="truncate"
                      title={selectedFile?.tahsil?.name}
                    >
                      {selectedFile?.tahsil?.name || '-'}
                    </Text>
                  </VStack>

                  <VStack align="start" spacing={1}>
                    <HStack spacing={2}>
                      <Icon as={FaFileExcel} color="green.500" boxSize={4} />
                      <Text
                        fontSize="sm"
                        color={subtitleColor}
                        fontWeight="medium"
                      >
                        File Type
                      </Text>
                    </HStack>
                    <Box width="100%" title={selectedFile?.fileType?.name}>
                      <Badge
                        colorScheme="green"
                        fontSize="xs"
                        px={2}
                        py={0.5}
                        maxW="100%"
                        display="inline-block"
                        className="truncate"
                      >
                        {getFileTypeDisplay(selectedFile?.fileType)}
                      </Badge>
                    </Box>
                  </VStack>

                  <VStack align="start" spacing={1}>
                    <HStack spacing={2}>
                      <Icon as={CalendarIcon} color="orange.500" boxSize={4} />
                      <Text
                        fontSize="sm"
                        color={subtitleColor}
                        fontWeight="medium"
                      >
                        Upload Date
                      </Text>
                    </HStack>
                    <Text fontWeight="semibold" color={textColor} fontSize="sm">
                      {formatDate(selectedFile.uploadedDate)}
                    </Text>
                  </VStack>
                </SimpleGrid>

                <Divider />

                <Flex
                  justify="space-between"
                  align="center"
                  p={{ base: 3, md: 4 }}
                  borderRadius="lg"
                  borderColor="gray.200"
                  direction={{ base: 'column', sm: 'row' }}
                  gap={{ base: 3, sm: 0 }}
                >
                  <HStack spacing={3} minW={0} flex="1">
                    <Avatar
                      size={{ base: 'xs', md: 'sm' }}
                      name={selectedFile.name}
                      bg="blue.500"
                    />
                    <VStack align="start" spacing={0} minW={0} flex="1">
                      <Text
                        fontSize="sm"
                        fontWeight="medium"
                        color={textColor}
                        className="truncate"
                        title={selectedFile.name || selectedFile.filename}
                        width="100%"
                      >
                        {getFileNameDisplay(
                          selectedFile.name || selectedFile.filename,
                        )}
                      </Text>
                      <Text fontSize="xs" color={subtitleColor}>
                        Click to download
                      </Text>
                    </VStack>
                  </HStack>
                  <Tooltip label="Download File" hasArrow>
                    <Button
                      colorScheme="blue"
                      size={{ base: 'sm', md: 'md' }}
                      leftIcon={<DownloadIcon />}
                      onClick={handleDownload}
                      borderRadius="md"
                      _hover={{ transform: 'translateY(-1px)' }}
                      transition="all 0.2s"
                      w={{ base: 'full', sm: 'auto' }}
                      flexShrink={0}
                    >
                      Download
                    </Button>
                  </Tooltip>
                </Flex>

                {isRejecting && (
                  <Box
                    p={{ base: 3, md: 4 }}
                    borderRadius="lg"
                    bg="red.50"
                    border="1px solid"
                    borderColor="red.200"
                  >
                    <Text fontWeight="semibold" color="red.700" mb={2}>
                      Rejection Notes Required
                    </Text>
                    <Text fontSize="sm" color="red.600" mb={3}>
                      Please provide a reason for rejecting this file
                    </Text>
                    <Textarea
                      id="reject-notes"
                      placeholder="Enter rejection reason..."
                      size="sm"
                      rows={3}
                      value={notes}
                      onChange={(e) => {
                        setNotes(e.target.value);
                        setNoteError('');
                      }}
                      borderColor={noteError ? 'red.300' : 'gray.300'}
                      _focus={{
                        borderColor: noteError ? 'red.500' : 'blue.500',
                        boxShadow: noteError
                          ? '0 0 0 1px var(--chakra-colors-red-500)'
                          : '0 0 0 1px var(--chakra-colors-blue-500)',
                      }}
                    />
                    {noteError && (
                      <Text
                        color="red.500"
                        fontSize="sm"
                        mt={2}
                        display="flex"
                        alignItems="center"
                      >
                        <Icon as={WarningIcon} mr={1} /> {noteError}
                      </Text>
                    )}
                  </Box>
                )}

                {selectedFile?.status === 'rejected' &&
                  selectedFile?.rejectionComments && (
                    <Alert
                      status="error"
                      variant="subtle"
                      borderRadius="lg"
                      alignItems="start"
                      flexDirection={{ base: 'column', sm: 'row' }}
                      textAlign={{ base: 'center', sm: 'left' }}
                      py={{ base: 3, md: 4 }}
                    >
                      <AlertIcon />
                      <Box ml={{ base: 0, sm: 3 }} mt={{ base: 2, sm: 0 }}>
                        <Text fontWeight="semibold" color="red.700" mb={1}>
                          Rejection Reason
                        </Text>
                        <Text color="red.600" fontSize="sm">
                          {selectedFile.rejectionComments}
                        </Text>
                      </Box>
                    </Alert>
                  )}

                {user?.role === 'schemeAdmin' &&
                  selectedFile?.status === 'submitted' && (
                    <Flex
                      gap={3}
                      mt={2}
                      direction={{ base: 'column', sm: 'row' }}
                    >
                      <Button
                        colorScheme="green"
                        size={{ base: 'md', md: 'lg' }}
                        flex="1"
                        leftIcon={<CheckCircleIcon />}
                        onClick={() => {
                          setIsRejecting(false);
                          handleAction(selectedFile._id, 'approved');
                        }}
                        isLoading={actionLoading === 'approved'}
                        isDisabled={actionLoading === 'rejected'}
                        borderRadius="lg"
                        bgGradient="linear(to-r, green.500, teal.500)"
                        _hover={{
                          bgGradient: 'linear(to-r, green.600, teal.600)',
                          transform: 'translateY(-1px)',
                          boxShadow: '0 10px 20px rgba(56, 178, 172, 0.3)',
                        }}
                        transition="all 0.2s"
                      >
                        Approve
                      </Button>
                      <Button
                        colorScheme="red"
                        size={{ base: 'md', md: 'lg' }}
                        flex="1"
                        leftIcon={<CloseIcon />}
                        onClick={
                          isRejecting
                            ? () => handleAction(selectedFile._id, 'rejected')
                            : () => setIsRejecting(true)
                        }
                        isLoading={actionLoading === 'rejected'}
                        isDisabled={actionLoading === 'approved'}
                        borderRadius="lg"
                        bgGradient="linear(to-r, red.500, orange.500)"
                        _hover={{
                          bgGradient: 'linear(to-r, red.600, orange.600)',
                          transform: 'translateY(-1px)',
                          boxShadow: '0 10px 20px rgba(245, 101, 101, 0.3)',
                        }}
                        transition="all 0.2s"
                      >
                        {isRejecting ? 'Confirm Reject' : 'Reject'}
                      </Button>
                    </Flex>
                  )}
              </Stack>
            ) : (
              <Center py={12}>
                <VStack spacing={4}>
                  <Box
                    w="80px"
                    h="80px"
                    borderRadius="full"
                    bg="gray.100"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Icon as={AttachmentIcon} w={10} h={10} color="gray.400" />
                  </Box>
                  <VStack spacing={2}>
                    <Text fontSize="lg" fontWeight="semibold" color={textColor}>
                      No file selected
                    </Text>
                    <Text color={subtitleColor} textAlign="center">
                      Select a file to view details
                    </Text>
                  </VStack>
                </VStack>
              </Center>
            )}
          </ModalBody>

          <ModalFooter
            pt={0}
            borderTop="1px solid"
            borderColor={borderColor}
            px={{ base: 4, md: 6 }}
            pb={{ base: 4, md: 6 }}
          >
            <Button
              onClick={onClose}
              colorScheme="gray"
              variant="outline"
              w="full"
              size={{ base: 'md', md: 'lg' }}
              borderRadius="lg"
              _hover={{
                // bg: 'gray.100',
                transform: 'translateY(-1px)',
              }}
              transition="all 0.2s"
              mt={1}
            >
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default FileDetailsModal;
